import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function ApplianceRepair() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/578174b567102a1a8bb6df37723dd480.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Appliance Repair Courses
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            Unlock Your Potential in the Appliance Repair Industry
          </p>
          <Button href="/contact" size="lg">
            Enroll Now
          </Button>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-6">Program Overview</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Explore the world of appliance repair through our comprehensive courses. From basic principles to advanced techniques, our program covers everything you need to kickstart a successful career in repairing major appliances.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With the increasing demand for skilled appliance repair technicians, this program provides you with the expertise to diagnose, repair, and maintain all types of household and commercial appliances.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=appliance%20repair%20training%20classroom%20students%20learning%20to%20fix%20refrigerators%20washers%20dryers%20hands%20on%20practice%20modern%20educational%20setting%20simple%20clean%20background%20bright%20lighting%20professional%20realistic%20photography&width=800&height=600&seq=appliance2&orientation=landscape"
                alt="Appliance Repair Training"
                className="rounded-lg shadow-xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Refrigerator Repair",
                description: "Master the diagnosis and repair of refrigerators, freezers, and ice makers, including compressor and cooling system issues."
              },
              {
                title: "Washer & Dryer Service",
                description: "Learn to troubleshoot and repair washing machines and dryers, from mechanical components to electronic controls."
              },
              {
                title: "Dishwasher Maintenance",
                description: "Understand dishwasher systems, water circulation, heating elements, and common repair procedures."
              },
              {
                title: "Oven & Range Repair",
                description: "Gain expertise in repairing electric and gas ovens, ranges, and cooktops, including heating elements and igniters."
              },
              {
                title: "Microwave Service",
                description: "Learn safe microwave repair techniques, including magnetron replacement and electrical system diagnostics."
              },
              {
                title: "Diagnostic Skills",
                description: "Develop advanced troubleshooting abilities using multimeters, diagnostic tools, and technical manuals."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-check-line text-2xl text-blue-900"></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Career Opportunities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              "Appliance Repair Technician",
              "Service Center Technician",
              "Independent Repair Business Owner",
              "Warranty Service Provider",
              "Commercial Appliance Specialist",
              "Field Service Technician"
            ].map((career, index) => (
              <div key={index} className="flex items-center bg-gray-50 p-6 rounded-lg">
                <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-briefcase-line text-xl text-blue-900"></i>
                </div>
                <span className="text-lg font-semibold text-black">{career}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-black text-center mb-12">Program Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Self-Paced Learning</h3>
              <p className="text-gray-700">Study at your own pace with flexible online learning options</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medal-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Certifications</h3>
              <p className="text-gray-700">
                ✔️ EPA Section 608 Certification – Required for handling refrigerants in sealed systems.<br/>
                ✔️ OSHA 10-Hour Certification – Ensures workplace safety knowledge.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Practical Experience</h3>
              <p className="text-gray-700">Learn with real appliance scenarios and industry-standard repair techniques</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Appliance Repair Career?</h2>
          <p className="text-xl text-white mb-8">
            Join our program today and become a skilled appliance repair professional.
          </p>
          <Button href="/contact" size="lg">
            Contact Us Today
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
